// Meeting Prep — one page the advisor reads in the car before a client meeting: talking points
// generated from evidence (coverage gaps, review state, open applications, renewals, moments),
// the family, what they hold and what is still open. Every point is an i18n key + params.
import { annualNow, diffDays, isActivePolicy, isTerminalStage, nextPolicyAnniversary, reviewStatus, type Day, type Person, type Policy, type Task, type WorkspaceView } from '@vero/domain';
import type { AttentionItem } from './index.ts';
import { coverageProfile } from './coverage.ts';
import { relationshipHealth, type Health } from './health.ts';
import { pendingDecisions } from './advisory.ts';

const x = (origin?: string | null) => (origin === 'commitment' ? 'prep.point_promise' : 'prep.point_waiting');

export interface PrepPoint { key: string; params: Record<string, string | number>; kind: 'gap' | 'review' | 'case' | 'renewal' | 'moment' | 'promise' | 'decision' | 'quote'; item?: AttentionItem }
export interface MeetingPrep {
  person: Person;
  health: Health | null;
  points: PrepPoint[];
  family: { person: Person; role: string }[];
  holdings: { policy: Policy; annual: number }[];
  openTasks: Task[];
  totalAnnual: number;
}

export function meetingPrep(v: WorkspaceView, personId: string, today: Day, attention: AttentionItem[]): MeetingPrep {
  const person = v.get('persons', personId)!;
  const points: PrepPoint[] = [];
  const cov = coverageProfile(v, personId, today);
  for (const a of cov.areas.filter((x) => x.gap).sort((x, y) => y.need - x.need)) points.push({ kind: 'gap', key: 'prep.point_gap', params: { area: `coverage.area.${a.area}`, reason: a.needReason } });
  const pols = v.where('policies', 'personId', personId);
  const rs = reviewStatus(person, pols, today);
  if (rs.state === 'overdue' || rs.state === 'never' || rs.state === 'due_soon') points.push({ kind: 'review', key: 'prep.point_review', params: { state: `enum.reviewState.${rs.state}` } });
  for (const c of v.where('cases', 'personId', personId).filter((c) => !isTerminalStage(c.stage))) points.push({ kind: 'case', key: 'prep.point_case', params: { product: c.product, stage: `enum.caseStage.${c.stage}` } });
  for (const p of pols.filter(isActivePolicy)) {
    const ann = nextPolicyAnniversary(p, today);
    if (ann && diffDays(today, ann) <= 60) points.push({ kind: 'renewal', key: 'prep.point_renewal', params: { product: p.productName, date: ann } });
  }
  for (const m of attention.filter((a) => a.personId === personId && (a.rule === 'birthday' || a.rule === 'nameday' || a.rule === 'child_milestone'))) points.push({ kind: 'moment', key: 'prep.point_moment', params: {}, item: m });
  // what we promised them and what we are waiting for (meeting commitments)
  for (const t of v.where('tasks', 'personId', personId).filter((x) => x.status === 'open' && (x.origin === 'commitment' || x.origin === 'waiting_client'))) {
    points.push({ kind: 'promise', key: x(t.origin), params: { text: t.title } });
  }
  for (const d of pendingDecisions(v, today).filter((d) => d.personId === personId)) points.push({ kind: 'decision', key: 'prep.point_decision', params: { title: d.title, days: d.days } });

  const family = person.householdId
    ? v.where('household_members', 'householdId', person.householdId).filter((m) => m.personId !== personId)
      .map((m) => ({ person: v.get('persons', m.personId), role: m.role as string })).filter((x): x is { person: Person; role: string } => !!x.person)
    : [];
  const holdings = pols.filter(isActivePolicy).map((policy) => ({ policy, annual: annualNow(v, policy, today) })).sort((a, b) => b.annual - a.annual);
  const openTasks = v.where('tasks', 'personId', personId).filter((t) => t.status === 'open').sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
  return {
    person, points, family, holdings, openTasks,
    health: person.stage === 'client' ? relationshipHealth(v, personId, today) : null,
    totalAnnual: holdings.reduce((s, h) => s + h.annual, 0),
  };
}
